import { apiClient } from './client';

// Types for collection chat
export interface CollectionMessage {
  id: string;
  collection_id: string;
  sender_address: string | null;
  is_system_message: boolean;
  content: string;
  created_at: string;
  deleted_at: string | null;
  sender_display_name: string | null;
}

// Parsed system message for sale events
export interface ParsedSaleMessage {
  type: 'SALE';
  nftTokenId: string;
  nftName: string;
  priceXRP: number;
  sellerAddress: string;
  buyerAddress: string;
}

// API Response types
interface MessagesResponse {
  success: boolean;
  messages: CollectionMessage[];
  hasMore: boolean;
}

interface SendMessageResponse {
  success: boolean;
  message: CollectionMessage;
}

interface PollResponse {
  success: boolean;
  hasNewMessages: boolean;
  newMessageCount: number;
  messages: CollectionMessage[];
}

interface UnreadCountResponse {
  success: boolean;
  unread_count: number;
}

interface RateLimitResponse {
  success: boolean;
  isLimited: boolean;
  secondsRemaining: number;
  rateLimitSeconds: number;
}

/**
 * Parse a system message content string into structured data
 * Format: SALE|nftTokenId|nftName|priceXRP|sellerAddress|buyerAddress
 */
export function parseSystemMessage(content: string): ParsedSaleMessage | null {
  const parts = content.split('|');
  if (parts[0] === 'SALE' && parts.length === 6) {
    return {
      type: 'SALE',
      nftTokenId: parts[1],
      nftName: parts[2],
      priceXRP: parseFloat(parts[3]),
      sellerAddress: parts[4],
      buyerAddress: parts[5],
    };
  }
  return null;
}

/**
 * Shorten a wallet address for display
 * @param address - Full wallet address
 * @param chars - Number of characters to show on each end (default 4)
 */
export function shortenAddress(address: string, chars = 4): string {
  if (address.length <= chars * 2 + 3) return address;
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Get messages in a collection chat (PUBLIC - no auth required)
 * @param collectionId - The collection UUID
 * @param limit - Max messages to return (default 100)
 * @param before - Get messages before this timestamp (ISO 8601 string)
 */
export async function getCollectionChatMessages(
  collectionId: string,
  limit = 100,
  before?: string
): Promise<{ messages: CollectionMessage[]; hasMore: boolean }> {
  let url = `/collection-chat/${collectionId}/messages?limit=${limit}`;
  if (before) {
    url += `&before=${encodeURIComponent(before)}`;
  }

  const response = await apiClient.get<MessagesResponse>(url);

  if (!response.success) {
    throw new Error('Failed to fetch collection chat messages');
  }

  return {
    messages: response.messages,
    hasMore: response.hasMore,
  };
}

/**
 * Send a message to a collection chat (AUTHENTICATED)
 * @param collectionId - The collection UUID
 * @param content - The message content (max 500 chars, no links)
 */
export async function sendCollectionChatMessage(
  collectionId: string,
  content: string
): Promise<CollectionMessage> {
  const response = await apiClient.post<SendMessageResponse>(
    `/collection-chat/${collectionId}/messages`,
    { content }
  );

  if (!response.success || !response.message) {
    throw new Error('Failed to send message');
  }

  return response.message;
}

/**
 * Poll for new messages in a collection chat (PUBLIC)
 * @param collectionId - The collection UUID
 * @param since - ISO 8601 timestamp to check for messages since
 */
export async function pollCollectionChat(
  collectionId: string,
  since: string
): Promise<PollResponse> {
  const url = `/collection-chat/${collectionId}/poll?since=${encodeURIComponent(since)}`;

  const response = await apiClient.get<PollResponse>(url);

  if (!response.success) {
    throw new Error('Failed to poll collection chat');
  }

  return response;
}

/**
 * Get unread message count for a collection chat (AUTHENTICATED)
 * @param collectionId - The collection UUID
 */
export async function getCollectionChatUnreadCount(collectionId: string): Promise<number> {
  const response = await apiClient.get<UnreadCountResponse>(
    `/collection-chat/${collectionId}/unread-count`
  );

  if (!response.success) {
    throw new Error('Failed to fetch unread count');
  }

  return response.unread_count;
}

/**
 * Mark a collection chat as read (AUTHENTICATED)
 * @param collectionId - The collection UUID
 */
export async function markCollectionChatAsRead(collectionId: string): Promise<void> {
  const response = await apiClient.patch<{ success: boolean }>(
    `/collection-chat/${collectionId}/read`
  );

  if (!response.success) {
    throw new Error('Failed to mark chat as read');
  }
}

/**
 * Get rate limit status for posting in a collection chat (AUTHENTICATED)
 * @param collectionId - The collection UUID
 */
export async function getCollectionChatRateLimitStatus(
  collectionId: string
): Promise<RateLimitResponse> {
  const response = await apiClient.get<RateLimitResponse>(
    `/collection-chat/${collectionId}/rate-limit-status`
  );

  if (!response.success) {
    throw new Error('Failed to get rate limit status');
  }

  return response;
}
